import { useAuth } from '@shared/components/AuthProvider';
import { useTranslation } from '@shared/core';
import { useFormik } from 'formik';
import { useState } from 'react';
import * as Yup from 'yup';
import { useFieldErrors } from '@shared/hooks/useFieldErrors';
import type { SwitchFormHandler } from '../AccountForm.types';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { isAuthSetupError } from '@static/js/authFailure';

type ForgotPasswordValues = {
  email: string;
};

type useForgotPasswordControllerProps = {
  switchForm: SwitchFormHandler;
};

export const useForgotPasswordController = ({ switchForm }: useForgotPasswordControllerProps) => {
  const auth = useAuth();
  const { t2, tq } = useTranslation('createWedding');
  const [submissionError, setSubmissionError] = useState<string>('');
  const errorTranslations = t2('signIn', 'errors');

  const switchToSignInForm = useEventCallback(() => switchForm('signIn'));

  const formik = useFormik<ForgotPasswordValues>({
    initialValues: { email: '' },
    validationSchema: Yup.object({
      email: Yup.string().email(errorTranslations.emailValid).required(errorTranslations.emailRequired),
    }).required(),
    onSubmit: async ({ email }) => {
      setSubmissionError('');
      try {
        await auth.loginManager.changePassword(email);
      } catch (err) {
        if (isAuthSetupError(err)) {
          setSubmissionError(tq`There was an error resetting your password. Please try again.`);
          return;
        }
        throw err;
      }
      switchToSignInForm();
    },
  });
  const getFieldErrorProps = useFieldErrors<ForgotPasswordValues>(formik);

  return { formik, submissionError, getFieldErrorProps };
};
